import { Star } from "lucide-react";
import { googleReviewSummary, googleReviews } from "@/data/googleReviews";

function formatRating(value: number) {
  return value.toLocaleString("fr-CA", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-CA", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}

function Stars({ rating, className = "h-5 w-5" }: { rating: number; className?: string }) {
  return (
    <span className="inline-flex gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${className} ${n <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-border"}`}
        />
      ))}
    </span>
  );
}

interface GoogleReviewsTrustProps {
  variant?: "band" | "compact";
  className?: string;
}

export function GoogleReviewsTrust({ variant = "band", className = "" }: GoogleReviewsTrustProps) {
  const { rating, reviewCount, verifiedAt } = googleReviewSummary;
  const ratingLabel = formatRating(rating);
  const dateLabel = formatDate(verifiedAt);
  const quotes = googleReviews.filter((r) => r.rating === 5 && r.text.trim()).slice(0, 3);

  if (variant === "compact") {
    return (
      <div className={`bg-white p-6 rounded-2xl shadow-xl border border-border max-w-[280px] ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Stars rating={rating} />
        </div>
        <p className="font-bold text-primary mb-1">
          {ratingLabel} sur 5 · {reviewCount} avis Google
        </p>
        <p className="text-xs text-muted-foreground">Vérifié le {dateLabel}</p>
      </div>
    );
  }

  return (
    <section
      className={`bg-white py-10 md:py-12 ${className}`}
      aria-labelledby="google-reviews-heading"
      data-testid="section-google-reviews-trust"
    >
      <div className="container mx-auto px-4 md:px-6">
      <div className="rounded-2xl border border-border p-5 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={`${import.meta.env.BASE_URL}google-g.png`}
              alt="Google"
              width={28}
              height={28}
              className="h-7 w-7 shrink-0"
              loading="lazy"
            />
            <h2 id="google-reviews-heading" className="text-2xl md:text-3xl font-bold text-primary">Votre plombier de référence à Longueuil</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-primary tabular-nums" data-testid="text-google-rating">
              {ratingLabel}/5
            </p>
            <Stars rating={rating} className="h-4 w-4" />
            <p className="text-sm text-muted-foreground" data-testid="text-google-count">{reviewCount} avis Google</p>
          </div>
        </div>

        {quotes.length > 0 && (
          <ul className="mt-5 grid gap-4 md:grid-cols-3">
            {quotes.map((q) => (
              <li key={q.id} className="rounded-xl bg-slate-50 p-5" data-testid={`card-google-review-${q.id}`}>
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary" aria-hidden="true">
                    {q.author.charAt(0).toUpperCase()}
                  </span>
                  <h3 className="text-sm font-semibold text-primary">{q.author}</h3>
                </div>
                <Stars rating={q.rating} className="h-4 w-4" />
                <span className="sr-only">Note : {q.rating} sur 5.</span>
                <blockquote cite={googleReviewSummary.sourceUrl} className="mt-2 text-sm text-foreground leading-relaxed">
                  « {q.text} »
                </blockquote>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-muted-foreground text-right">
          Relevé le <time dateTime={verifiedAt}>{dateLabel}</time>
        </p>
      </div>
      </div>
    </section>
  );
}
